import type { ModalCtaStyle } from './modal-blocks/modal-block-types'

/** An app store whose official badge the dashboard banner can draw for a CTA. */
export type AppStore = 'app_store' | 'google_play'

const STORE_BY_HOST: Readonly<Record<string, AppStore>> = {
  'apps.apple.com': 'app_store',
  'play.google.com': 'google_play',
}

/**
 * The app store a CTA link points at, so the dashboard banner can draw that
 * store's badge for it instead of a text button. `null` keeps the text button.
 *
 * The link is parsed, not matched as a string. A substring test would accept
 * `https://apps.apple.com.evil.com/` or a path that only mentions the host, and
 * the badge would then vouch for a destination that is not the store's. Only
 * https counts: the badge promises the official store, and plain http is not that.
 *
 * The admin form and the customer carousel both call this, so the badge in the
 * preview and the badge customers see cannot disagree on one link. It trims for
 * them too, so a form field with a stray space cannot disagree with the saved link.
 */
export function storeForLink(link: string | undefined): AppStore | null {
  const trimmed = link?.trim() ?? ''

  if (trimmed === '') {
    return null
  }

  try {
    const url = new URL(trimmed)

    if (url.protocol !== 'https:') {
      return null
    }

    return STORE_BY_HOST[url.hostname] ?? null
  } catch {
    // A link that does not parse as an absolute URL is an in-app path or a typo.
    // Neither is a store listing, so the caller keeps its normal button.
    return null
  }
}

/**
 * Whether a flat CTA (banner, toaster, strip) renders at all. A store link
 * needs no text, because the badge's artwork carries its own words. Any other
 * CTA with no text would be a blank button, so it is dropped.
 */
export const ctaIsVisible = (text: string, link: string): boolean =>
  text.trim() !== '' || storeForLink(link) !== null

/**
 * The store whose badge a modal CTA block draws, or `null` for a button or link.
 * An inline link stays text even to a store: the operator picked the look of a
 * link inside the copy, and a badge would break out of it.
 */
export const modalCtaStore = (style: ModalCtaStyle, link: string): AppStore | null =>
  style === 'inline_link' ? null : storeForLink(link)

/**
 * Sizes a text button that sits in a row with a badge. The design gives both
 * CTAs the badge's 40px height, but a `medium` Button is 32px, so the row looked
 * mismatched. The 16px side padding is the design's value too.
 */
const STORE_BADGE_PAIRED_BUTTON_CLASS = 'h-10 px-4'

/**
 * The class for every text button in a CTA row, given the row's links. A row
 * with no store link keeps the buttons' own size.
 */
export const storeBadgeRowButtonClass = (
  ...links: readonly (string | undefined)[]
): string | undefined =>
  links.some((link) => storeForLink(link) !== null) ? STORE_BADGE_PAIRED_BUTTON_CLASS : undefined
